import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import { TextField } from "../../../../ui/components/TextField";
import {
  FeatherPlus,
  FeatherRefreshCw,
  FeatherServer,
  FeatherX,
} from "@subframe/core";
import {
  listTenantConfigs,
  provisionTenant,
  retryTenant,
  TenantConfigDTO,
  ProvisionStatus,
} from "../../data/api/provisioningApi";

const STATUS_BADGE: Record<
  ProvisionStatus,
  { variant: "brand" | "neutral" | "error" | "warning" | "success"; label: string }
> = {
  provisioning: { variant: "warning", label: "Provisioning…" },
  ready: { variant: "success", label: "Ready" },
  active: { variant: "brand", label: "Active" },
  inactive: { variant: "neutral", label: "Inactive" },
  failed: { variant: "error", label: "Failed" },
};

export const ProvisioningPage: React.FC = () => {
  const [configs, setConfigs] = useState<TenantConfigDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await listTenantConfigs();
      setConfigs(res.data?.configs ?? []);
    } catch (err: any) {
      setError(err.message || "Failed to load provisioned tenants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleProvision = async () => {
    const value = tenantId.trim();
    if (!value) {
      setFormError("Enter a subdomain / tenant id (e.g. acme).");
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      setSuccessMsg(null);
      const res = await provisionTenant(value);
      setSuccessMsg(
        `Tenant "${res.data.config.tenant_id}" provisioned — DB "${res.data.config.db_name}" and bucket "${res.data.config.s3_bucket}" are ready. You can now add the company.`
      );
      setTenantId("");
      setShowForm(false);
      await fetchConfigs();
    } catch (err: any) {
      setFormError(err.message || "Provisioning failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async (id: string) => {
    try {
      setRetryingId(id);
      setError(null);
      await retryTenant(id);
      await fetchConfigs();
    } catch (err: any) {
      setError(err.message || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="flex h-screen w-full flex-col bg-default-background">
      <div className="flex w-full flex-col items-start gap-6 p-12">
        {/* Header */}
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Provision Tenants
            </span>
            <span className="text-body font-body text-subtext-color">
              Create the tenant database and S3 credentials before adding a company.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-secondary"
              icon={<FeatherRefreshCw />}
              onClick={fetchConfigs}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              icon={showForm ? <FeatherX /> : <FeatherPlus />}
              onClick={() => {
                setShowForm((s) => !s);
                setFormError(null);
              }}
            >
              {showForm ? "Cancel" : "Provision Tenant"}
            </Button>
          </div>
        </div>

        {/* Success banner */}
        {successMsg && (
          <div className="flex w-full flex-col items-start gap-1 rounded-lg border border-solid border-green-200 bg-green-50 p-4">
            <span className="text-sm font-medium text-green-800">{successMsg}</span>
          </div>
        )}

        {/* Inline provision form */}
        {showForm && (
          <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              New tenant
            </span>
            <div className="flex w-full max-w-md flex-col items-start gap-2">
              <TextField
                className="w-full"
                variant="outline"
                label="Subdomain / Tenant ID"
                helpText="Lowercase letters, digits and hyphens (3–50 chars). Creates bucket zournal-client-{id} and DB zournal_{id}."
                error={!!formError}
                icon={<FeatherServer />}
              >
                <TextField.Input
                  placeholder="acme"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !submitting) handleProvision();
                  }}
                />
              </TextField>
              {formError && (
                <span className="text-sm font-medium text-error-700">
                  {formError}
                </span>
              )}
            </div>
            <Button
              onClick={handleProvision}
              loading={submitting}
              disabled={submitting}
            >
              Provision
            </Button>
          </div>
        )}

        {/* List error */}
        {error && (
          <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
            <span className="text-sm font-medium text-red-800">{error}</span>
          </div>
        )}

        {/* Loading */}
        {loading && configs.length === 0 ? (
          <div className="flex w-full items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading tenants...</p>
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col items-start gap-4">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Tenant ID</Table.HeaderCell>
                  <Table.HeaderCell>Database</Table.HeaderCell>
                  <Table.HeaderCell>S3 Bucket</Table.HeaderCell>
                  <Table.HeaderCell>Driver</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {configs.map((cfg) => {
                const badge = STATUS_BADGE[cfg.status] ?? STATUS_BADGE.inactive;
                return (
                  <Table.Row key={cfg.id}>
                    <Table.Cell>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {cfg.tenant_id || "-"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-subtext-color">
                        {cfg.db_name || "-"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-subtext-color">
                        {cfg.s3_bucket || "-"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-subtext-color">
                        {cfg.provision_driver || "-"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-col items-start gap-1">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                        {cfg.status === "failed" && cfg.provision_error && (
                          <span
                            className="text-caption font-caption text-error-700 max-w-xs truncate"
                            title={cfg.provision_error}
                          >
                            {cfg.provision_error}
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {cfg.status === "failed" ? (
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherRefreshCw />}
                          onClick={() => handleRetry(cfg.tenant_id)}
                          loading={retryingId === cfg.tenant_id}
                          disabled={retryingId === cfg.tenant_id}
                        >
                          Retry
                        </Button>
                      ) : (
                        <span className="text-caption font-caption text-subtext-color">
                          {cfg.company_id
                            ? `Company #${cfg.company_id}`
                            : cfg.status === "ready"
                            ? "Awaiting company"
                            : "—"}
                        </span>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table>

            {!loading && configs.length === 0 && (
              <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-neutral-border py-12">
                <span className="text-body font-body text-subtext-color">
                  No tenants provisioned yet. Click "Provision Tenant" to create one.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProvisioningPage;
